// Display-only mirror of the server's authoritative Training School formulas
// (server/src/main/java/com/projectpets/progression/TrainingService.java: `costFor`/`durationFor`).
// The server ALWAYS recomputes both from its own copy at POST /training time (§12.1) — a drift
// here could only ever mislead this screen's own cost summary, never let a player pay less than
// the real charge. trainingMath.test.ts pins the same numeric parity values the server's own
// TrainingServiceTest pins, so the two formulas are kept honest against each other.

/** `ceil(12 * v^1.5 / 5) * 5` — BALANCE-normative superlinear Shells cost. */
export function trainingCostFor(statValue: number): number {
  const raw = 12 * Math.pow(statValue, 1.5);
  return Math.ceil(raw / 5) * 5;
}

/** BALANCE-normative duration tiers: 1-19 -> "15 min", 20-39 -> "1 h", 40-59 -> "3 h",
 *  60-79 -> "5 h", 80+ -> "8 h". */
export function trainingDurationLabelFor(statValue: number): string {
  if (statValue < 20) return '15 min';
  if (statValue < 40) return '1 h';
  if (statValue < 60) return '3 h';
  if (statValue < 80) return '5 h';
  return '8 h';
}

// Ability-slots-and-training-schools Task 10 addition: an ability lesson's own duration tiers,
// keyed by the ability's `levelReq` (server: TrainingService's lesson-kind `durationFor`) —
// 1/5 -> 15 min, 10 -> 1 h, 15 -> 3 h, 20 -> 5 h, 30 -> 8 h. Same "display-only mirror, the
// server always recomputes the real duration at POST /training time" rule `trainingDurationLabelFor`
// above already documents; unlike the stat side this is a lookup, not a formula, because
// `levelReq` only ever takes those six exact values (the six slot-unlock levels).
const LESSON_DURATION_BY_LEVEL_REQ: Record<number, string> = {
  1: '15 min',
  5: '15 min',
  10: '1 h',
  15: '3 h',
  20: '5 h',
  30: '8 h',
};

export function lessonDurationLabelFor(levelReq: number): string {
  return LESSON_DURATION_BY_LEVEL_REQ[levelReq] ?? '';
}

// Task 11 fix-wave addition, shared by `AbilitySlotsSheet.tsx` and `TrainingScreen.tsx`'s own
// respec confirm: a slot's occupant (`AbilitySlotDto.abilityCode`) is a raw code, and the only
// place with a real name for it is `LearnableAbility.name` for WHOEVER'S element it belongs to —
// which the displaced ability's own element may not be (that's exactly the respec case: swapping
// one element's ability out for another's). Content is data, never code: this is generic string
// formatting (split on "_", title-case each word), never a switch/if keyed on a specific ability
// code — the only way either caller shows anything nicer than the raw code for an ability it has
// no catalog entry for.
export function humanizeAbilityCode(code: string): string {
  return code
    .split('_')
    .map((word) => (word.length === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join(' ');
}
